/** Shared scalar and small-shape helpers for the pull serializer. */

/**
 * Render an input/field value so the parser reads it back verbatim.
 * `undefined` (no default declared) renders nothing after the colon —
 * `- name:` — which `parseInputs` reads back as `defaultValue: undefined`,
 * not `''`. Collapsing "omitted" into `'""'` here would make `spec pull`
 * regenerate the shadowing empty default on every pull → edit → push cycle
 * (see workflow-field-diff.ts for the stored-vs-omitted distinction this
 * mirrors).
 */
export function inputValue(v: string | undefined): string {
  if (v === undefined) return '';
  return v === '' ? '""' : v;
}

/** Render `text` as a markdown blockquote, one `> ` per line. */
export function blockquote(text: string): string {
  return text
    .trim()
    .split('\n')
    .map((l) => `> ${l}`)
    .join('\n');
}

/** Quote a YAML scalar only when plain rendering would be ambiguous. */
export function yamlScalar(s: string): string {
  if (s === '') return '""';
  if (/^[A-Za-z0-9 _\-./@]+$/.test(s) && !/^\s|\s$/.test(s)) return s;
  return JSON.stringify(s);
}

export function strRecord(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (v && typeof v === 'object') {
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (typeof val === 'string') out[k] = val;
    }
  }
  return out;
}

export function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

export function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

export function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

export function prop(v: unknown, key: string): unknown {
  return v && typeof v === 'object' ? (v as Record<string, unknown>)[key] : undefined;
}

export function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}
